import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  InputAdornment,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  MergeType as MergeIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import ProductMergeDialog from './ProductMergeDialog';
import ProductDocumentsPanel, { StagedProductDocument, openProductDocument, uploadProductDocument } from './ProductDocumentsPanel';
import { filterActiveIngredientOptions } from '../../utils/activeIngredientSearch';

interface ProductListItem {
  id: string;
  name: string;
  manufacturerName?: string;
  formulationAcronym?: string;
  dangerousGoods: boolean;
  archivedDate?: string;
  expectedMinRate?: number;
  expectedMaxRate?: number;
  expectedRateUnitName?: string;
  activeIngredients: string[];
  categories: string[];
  msdsDocumentId?: string;
  labelDocumentId?: string;
}

// Every column sorts on the server (GET api/products' sortBy), so the order
// holds across pages.
type SortKey = 'name' | 'manufacturer' | 'formulation' | 'actives' | 'category' | 'minRate' | 'maxRate' | 'rateUnit' | 'dangerousGoods' | 'docs' | 'status';

interface ProductDetail {
  id: string;
  name: string;
  manufacturerId?: string;
  formulationId?: string;
  weightVolume?: number;
  packagingGroup?: string;
  unNumber?: string;
  dangerousGoods: boolean;
  labelReferenceNumber?: string;
  defaultUseMonth?: number;
  defaultUseDay?: number;
  displayProductId?: string;
  expectedMinRate?: number;
  expectedMaxRate?: number;
  expectedRateUnitId?: string;
  geneStackId?: string;
  activeIngredients: ProductActiveIngredient[];
  categories: ProductCategoryLink[];
}

interface ProductActiveIngredient {
  id: string;
  activeIngredientId: string;
  activeIngredientName: string;
  unitId: string;
  unitName: string;
  unitAmount: number;
  isVisible: boolean;
}

interface ProductCategoryLink {
  id: string;
  productCategoryId: string;
  productCategoryName: string;
  relationIndex: number;
}

interface LookupItem {
  id: string;
  name: string;
}

interface ProductFormData {
  name: string;
  manufacturerId: string;
  formulationId: string;
  weightVolume: string;
  packagingGroup: string;
  unNumber: string;
  dangerousGoods: boolean;
  labelReferenceNumber: string;
  defaultUseMonth: string;
  defaultUseDay: string;
  displayProductId: string;
  expectedMinRate: string;
  expectedMaxRate: string;
  expectedRateUnitId: string;
  geneStackId: string;
}

const emptyFormData: ProductFormData = {
  name: '',
  manufacturerId: '',
  formulationId: '',
  weightVolume: '',
  packagingGroup: '',
  unNumber: '',
  dangerousGoods: false,
  labelReferenceNumber: '',
  defaultUseMonth: '',
  defaultUseDay: '',
  displayProductId: '',
  expectedMinRate: '',
  expectedMaxRate: '',
  expectedRateUnitId: '',
  geneStackId: '',
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MAX_INLINE_CHIPS = 2;
const chipSx = { height: 20, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } };

const ChipOverflow: React.FC<{
  values: string[];
  color?: 'default' | 'primary' | 'secondary';
  maxChips?: number;
  chipMaxWidth?: number;
}> = ({ values, color = 'default', maxChips = MAX_INLINE_CHIPS, chipMaxWidth }) => {
  if (values.length === 0) return <>-</>;
  const shown = values.slice(0, maxChips);
  const extra = values.length - shown.length;
  const truncatedChipSx = chipMaxWidth
    ? { ...chipSx, maxWidth: chipMaxWidth, '& .MuiChip-label': { ...chipSx['& .MuiChip-label'], overflow: 'hidden', textOverflow: 'ellipsis' } }
    : chipSx;
  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {shown.map((v) => (
        <Tooltip key={v} title={chipMaxWidth ? v : ''}>
          <Chip label={v} size="small" color={color} variant="outlined" sx={truncatedChipSx} />
        </Tooltip>
      ))}
      {extra > 0 && (
        <Tooltip title={values.slice(maxChips).join(', ')}>
          <Chip label={`+${extra}`} size="small" variant="outlined" sx={chipSx} />
        </Tooltip>
      )}
    </Box>
  );
};

const ProductManagement: React.FC = () => {
  const auth = useAuth();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [manufacturers, setManufacturers] = useState<LookupItem[]>([]);
  const [formulations, setFormulations] = useState<LookupItem[]>([]);
  const [units, setUnits] = useState<LookupItem[]>([]);
  const [activeIngredients, setActiveIngredients] = useState<LookupItem[]>([]);
  const [geneStacks, setGeneStacks] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [tab, setTab] = useState<'all' | 'no-actives'>('all');
  const [sortBy, setSortBy] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(emptyFormData);

  // Active-ingredient rows in the create/edit dialog. In edit mode each add/
  // remove hits the API immediately (persisted: true) same as before; in
  // create mode there's no ProductId yet, so rows are staged locally
  // (persisted: false) and flushed to the API right after the product itself
  // is created in handleSubmit.
  const [activeRows, setActiveRows] = useState<(ProductActiveIngredient & { persisted: boolean })[]>([]);
  const [newAiId, setNewAiId] = useState('');
  const [newUnitId, setNewUnitId] = useState('');
  const [newUnitAmount, setNewUnitAmount] = useState('0');

  // Category rows in the create/edit dialog — same persisted/staged pattern as
  // activeRows above (edit mode hits the API immediately, create mode stages
  // locally and flushes after the product itself is created in handleSubmit).
  const [productCategories, setProductCategories] = useState<LookupItem[]>([]);
  const [categoryRows, setCategoryRows] = useState<(ProductCategoryLink & { persisted: boolean })[]>([]);
  const [newCategoryId, setNewCategoryId] = useState('');

  // MSDS/label PDFs picked while creating a product — uploaded once it
  // exists (edit mode uploads straight away, inside ProductDocumentsPanel).
  const [stagedDocuments, setStagedDocuments] = useState<StagedProductDocument[]>([]);

  // The product a "Merge with another product" was started from.
  const [mergeProduct, setMergeProduct] = useState<ProductListItem | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` });

  // Debounce the search box so we don't fire a request per keystroke.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        includeArchived: String(includeArchived),
        page: String(page + 1),
        pageSize: String(pageSize),
        sortBy,
        sortDir,
      });
      if (search) params.set('search', search);
      if (tab === 'no-actives') params.set('noActiveIngredients', 'true');
      const response = await fetch(`/api/products?${params.toString()}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [includeArchived, page, pageSize, sortBy, sortDir, search, tab, auth.tokens?.accessToken]);

  const fetchLookups = async () => {
    try {
      const [manuRes, formRes, unitRes, aiRes, geneStackRes, categoryRes] = await Promise.all([
        fetch('/api/manufacturers', { headers: authHeaders() }),
        fetch('/api/formulations', { headers: authHeaders() }),
        fetch('/api/units', { headers: authHeaders() }),
        fetch('/api/active-ingredients', { headers: authHeaders() }),
        fetch('/api/gene-stacks', { headers: authHeaders() }),
        fetch('/api/product-categories', { headers: authHeaders() }),
      ]);
      if (manuRes.ok) setManufacturers(await manuRes.json());
      if (formRes.ok) setFormulations(await formRes.json());
      if (unitRes.ok) setUnits(await unitRes.json());
      if (aiRes.ok) setActiveIngredients(await aiRes.json());
      if (geneStackRes.ok) setGeneStacks(await geneStackRes.json());
      if (categoryRes.ok) setProductCategories(await categoryRes.json());
    } catch {
      // best-effort — dropdowns just stay empty if this fails
    }
  };

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchProducts();
    }
  }, [fetchProducts, auth.isSignedIn]);

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchLookups();
    }
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  const fetchProductDetail = async (id: string): Promise<ProductDetail | null> => {
    const response = await fetch(`/api/products/${id}`, { headers: authHeaders() });
    if (!response.ok) return null;
    return response.json();
  };

  const handleOpenDialog = async (productId?: string) => {
    if (productId) {
      const detail = await fetchProductDetail(productId);
      if (!detail) {
        setError('Failed to load product details');
        return;
      }
      setEditingProductId(detail.id);
      setFormData({
        name: detail.name,
        manufacturerId: detail.manufacturerId || '',
        formulationId: detail.formulationId || '',
        weightVolume: detail.weightVolume != null ? String(detail.weightVolume) : '',
        packagingGroup: detail.packagingGroup || '',
        unNumber: detail.unNumber || '',
        dangerousGoods: detail.dangerousGoods,
        labelReferenceNumber: detail.labelReferenceNumber || '',
        defaultUseMonth: detail.defaultUseMonth != null ? String(detail.defaultUseMonth) : '',
        defaultUseDay: detail.defaultUseDay != null ? String(detail.defaultUseDay) : '',
        displayProductId: detail.displayProductId || '',
        expectedMinRate: detail.expectedMinRate != null ? String(detail.expectedMinRate) : '',
        expectedMaxRate: detail.expectedMaxRate != null ? String(detail.expectedMaxRate) : '',
        expectedRateUnitId: detail.expectedRateUnitId || '',
        geneStackId: detail.geneStackId || '',
      });
      setActiveRows(detail.activeIngredients.map((pai) => ({ ...pai, persisted: true })));
      setCategoryRows(detail.categories.map((c) => ({ ...c, persisted: true })));
    } else {
      setEditingProductId(null);
      setFormData(emptyFormData);
      setActiveRows([]);
      setCategoryRows([]);
    }
    setNewAiId('');
    setNewUnitId('');
    setNewUnitAmount('0');
    setNewCategoryId('');
    setStagedDocuments([]);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProductId(null);
    setFormData(emptyFormData);
    setActiveRows([]);
    setCategoryRows([]);
    setStagedDocuments([]);
  };

  // Edit mode: hits the API immediately. Create mode: no ProductId exists
  // yet, so the row is just staged in local state.
  const handleAddActiveRow = async () => {
    if (!newAiId || !newUnitId) return;
    if (activeRows.some((r) => r.activeIngredientId === newAiId)) {
      setError('This active ingredient is already added.');
      return;
    }

    if (editingProductId) {
      try {
        const response = await fetch(`/api/products/${editingProductId}/active-ingredients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ activeIngredientId: newAiId, unitId: newUnitId, unitAmount: Number(newUnitAmount), isVisible: true }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const fieldErrors = data.errors ? Object.values(data.errors).flat() : [];
          throw new Error(data.error || fieldErrors.join(' ') || 'Failed to add active ingredient');
        }
        setActiveRows((prev) => [...prev, { ...data, persisted: true }]);
        await fetchProducts(); // keep the list's Actives column in sync
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        return;
      }
    } else {
      setActiveRows((prev) => [
        ...prev,
        {
          id: `pending-${Date.now()}-${Math.random()}`,
          activeIngredientId: newAiId,
          activeIngredientName: activeIngredients.find((a) => a.id === newAiId)?.name || '',
          unitId: newUnitId,
          unitName: units.find((u) => u.id === newUnitId)?.name || '',
          unitAmount: Number(newUnitAmount),
          isVisible: true,
          persisted: false,
        },
      ]);
    }
    setNewAiId('');
    setNewUnitId('');
    setNewUnitAmount('0');
  };

  const handleRemoveActiveRow = async (row: ProductActiveIngredient & { persisted: boolean }) => {
    if (row.persisted && editingProductId) {
      try {
        const response = await fetch(`/api/products/${editingProductId}/active-ingredients/${row.id}`, { method: 'DELETE', headers: authHeaders() });
        if (!response.ok) throw new Error('Failed to remove active ingredient');
        await fetchProducts();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        return;
      }
    }
    setActiveRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  // Edit mode: hits the API immediately. Create mode: staged locally, same as
  // handleAddActiveRow above.
  const handleAddCategoryRow = async () => {
    if (!newCategoryId) return;
    if (categoryRows.some((r) => r.productCategoryId === newCategoryId)) {
      setError('This category is already added.');
      return;
    }

    if (editingProductId) {
      try {
        const response = await fetch(`/api/products/${editingProductId}/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ productCategoryId: newCategoryId }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const fieldErrors = data.errors ? Object.values(data.errors).flat() : [];
          throw new Error(data.error || fieldErrors.join(' ') || 'Failed to add category');
        }
        setCategoryRows((prev) => [...prev, { ...data, persisted: true }]);
        await fetchProducts(); // keep the list's Category column in sync
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        return;
      }
    } else {
      setCategoryRows((prev) => [
        ...prev,
        {
          id: `pending-${Date.now()}-${Math.random()}`,
          productCategoryId: newCategoryId,
          productCategoryName: productCategories.find((c) => c.id === newCategoryId)?.name || '',
          relationIndex: 100,
          persisted: false,
        },
      ]);
    }
    setNewCategoryId('');
  };

  const handleRemoveCategoryRow = async (row: ProductCategoryLink & { persisted: boolean }) => {
    if (row.persisted && editingProductId) {
      try {
        const response = await fetch(`/api/products/${editingProductId}/categories/${row.productCategoryId}`, { method: 'DELETE', headers: authHeaders() });
        if (!response.ok) throw new Error('Failed to remove category');
        await fetchProducts();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        return;
      }
    }
    setCategoryRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  const handleSubmit = async () => {
    try {
      const url = editingProductId ? `/api/products/${editingProductId}` : '/api/products';
      const method = editingProductId ? 'PUT' : 'POST';
      const payload = {
        name: formData.name,
        manufacturerId: formData.manufacturerId || null,
        formulationId: formData.formulationId || null,
        weightVolume: formData.weightVolume ? Number(formData.weightVolume) : null,
        packagingGroup: formData.packagingGroup || null,
        unNumber: formData.unNumber || null,
        dangerousGoods: formData.dangerousGoods,
        labelReferenceNumber: formData.labelReferenceNumber || null,
        defaultUseMonth: formData.defaultUseMonth ? Number(formData.defaultUseMonth) : null,
        defaultUseDay: formData.defaultUseDay ? Number(formData.defaultUseDay) : null,
        displayProductId: formData.displayProductId || null,
        expectedMinRate: formData.expectedMinRate ? Number(formData.expectedMinRate) : null,
        expectedMaxRate: formData.expectedMaxRate ? Number(formData.expectedMaxRate) : null,
        expectedRateUnitId: formData.expectedRateUnitId || null,
        geneStackId: formData.geneStackId || null,
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const fieldErrors = errorData.errors ? Object.values(errorData.errors).flat() : [];
        throw new Error(errorData.error || fieldErrors.join(' ') || 'Failed to save product');
      }

      // Flush the staged active-ingredient rows now that the product exists.
      if (!editingProductId) {
        const created = await response.json();
        for (const row of activeRows) {
          const aiResponse = await fetch(`/api/products/${created.id}/active-ingredients`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ activeIngredientId: row.activeIngredientId, unitId: row.unitId, unitAmount: row.unitAmount, isVisible: true }),
          });
          if (!aiResponse.ok) {
            const errData = await aiResponse.json().catch(() => ({}));
            throw new Error(`Product "${formData.name}" was created, but failed to add active ingredient "${row.activeIngredientName}": ${errData.error || 'unknown error'}`);
          }
        }
        for (const row of categoryRows) {
          const catResponse = await fetch(`/api/products/${created.id}/categories`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ productCategoryId: row.productCategoryId }),
          });
          if (!catResponse.ok) {
            const errData = await catResponse.json().catch(() => ({}));
            throw new Error(`Product "${formData.name}" was created, but failed to add category "${row.productCategoryName}": ${errData.error || 'unknown error'}`);
          }
        }
        for (const doc of stagedDocuments) {
          try {
            await uploadProductDocument(created.id, doc.documentType, doc.file, doc.issueDate, authHeaders());
          } catch (err) {
            throw new Error(`Product "${formData.name}" was created, but failed to upload "${doc.file.name}": ${err instanceof Error ? err.message : 'unknown error'}`);
          }
        }
      }

      handleCloseDialog();
      await fetchProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Archive this product?')) return;
    try {
      const response = await fetch(`/api/products/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to archive product');
      await fetchProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(`/api/products/${id}/restore`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to restore product');
      await fetchProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const sortableHeader = (key: SortKey, label: string, align?: 'right') => (
    <TableCell align={align} sortDirection={sortBy === key ? sortDir : false}>
      <TableSortLabel active={sortBy === key} direction={sortBy === key ? sortDir : 'asc'} onClick={() => handleSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const documentChip = (productId: string, documentId: string, label: string) => (
    <Tooltip title={`Open ${label} PDF`}>
      <Chip
        label={label}
        size="small"
        variant="outlined"
        color="primary"
        icon={<PdfIcon />}
        onClick={() =>
          openProductDocument(productId, documentId, authHeaders())
            .catch((err) => setError(err instanceof Error ? err.message : 'An error occurred'))
        }
        sx={{ ...chipSx, '& .MuiChip-icon': { fontSize: 13 } }}
      />
    </Tooltip>
  );

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <InventoryIcon />
          Product Management
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControlLabel
            control={<Switch checked={includeArchived} onChange={(e) => { setPage(0); setIncludeArchived(e.target.checked); }} />}
            label="Show Archived"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Product
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, newTab) => { setPage(0); setTab(newTab); }}
        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
      >
        <Tab value="all" label="Products" />
        <Tab value="no-actives" label="Products without Actives" />
      </Tabs>

      <TextField
        fullWidth
        size="small"
        placeholder="Search by product name, manufacturer, formulation, or active ingredient..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && products.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {tab === 'no-actives' ? 'Every product has at least one active ingredient assigned.' : 'No products found.'}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.8125rem' },
                '& .MuiTableCell-head': { fontWeight: 600 },
                // An arrow only takes up room on the sorted column, so all
                // these columns fit a normal screen without scrolling sideways.
                '& .MuiTableSortLabel-root': { position: 'relative' },
                '& .MuiTableSortLabel-root:not(.Mui-active) .MuiTableSortLabel-icon': { position: 'absolute', left: '100%', ml: 0 },
                '& .MuiTableCell-alignRight .MuiTableSortLabel-root:not(.Mui-active) .MuiTableSortLabel-icon': { left: 'auto', right: '100%', mr: 0 },
              }}
            >
              <TableHead>
                <TableRow>
                  {sortableHeader('name', 'Name')}
                  {sortableHeader('manufacturer', 'Manufacturer')}
                  {sortableHeader('formulation', 'Formulation')}
                  {sortableHeader('actives', 'Actives')}
                  {sortableHeader('category', 'Category')}
                  {sortableHeader('minRate', 'Min Rate', 'right')}
                  {sortableHeader('maxRate', 'Max Rate', 'right')}
                  {sortableHeader('rateUnit', 'Rate Unit')}
                  {sortableHeader('dangerousGoods', 'Dangerous Goods')}
                  {sortableHeader('docs', 'Docs')}
                  {sortableHeader('status', 'Status')}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell sx={{ minWidth: 180 }}>{product.name}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Tooltip title={product.manufacturerName || ''}>
                        <Typography variant="inherit" noWrap>{product.manufacturerName || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{product.formulationAcronym || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 150 }}>
                      <ChipOverflow values={product.activeIngredients} color="primary" maxChips={1} chipMaxWidth={110} />
                    </TableCell>
                    <TableCell>
                      <ChipOverflow values={product.categories} color="secondary" />
                    </TableCell>
                    <TableCell align="right">{product.expectedMinRate ?? '-'}</TableCell>
                    <TableCell align="right">{product.expectedMaxRate ?? '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{product.expectedRateUnitName || '-'}</TableCell>
                    <TableCell>
                      {product.dangerousGoods ? <Chip label="Yes" size="small" color="warning" sx={chipSx} /> : '-'}
                    </TableCell>
                    <TableCell>
                      {product.msdsDocumentId || product.labelDocumentId ? (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {product.msdsDocumentId && documentChip(product.id, product.msdsDocumentId, 'MSDS')}
                          {product.labelDocumentId && documentChip(product.id, product.labelDocumentId, 'Label')}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {product.archivedDate ? (
                        <Chip label="Archived" size="small" color="default" sx={chipSx} />
                      ) : (
                        <Chip label="Active" size="small" color="success" sx={chipSx} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {product.archivedDate ? (
                        <Tooltip title="Restore Product">
                          <IconButton size="small" onClick={() => handleRestore(product.id)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Edit Product">
                            <IconButton size="small" onClick={() => handleOpenDialog(product.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Merge with another product">
                            <IconButton size="small" onClick={() => setMergeProduct(product)}>
                              <MergeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive Product">
                            <IconButton size="small" color="error" onClick={() => handleArchive(product.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>
      )}

      <ProductMergeDialog
        product={mergeProduct}
        onClose={() => setMergeProduct(null)}
        onMerged={() => { setMergeProduct(null); fetchProducts(); }}
      />

      {/* Create/Edit Product Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingProductId ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Manufacturer</InputLabel>
                <Select
                  value={formData.manufacturerId}
                  label="Manufacturer"
                  onChange={(e) => setFormData({ ...formData, manufacturerId: e.target.value })}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {manufacturers.map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Formulation</InputLabel>
                <Select
                  value={formData.formulationId}
                  label="Formulation"
                  onChange={(e) => setFormData({ ...formData, formulationId: e.target.value })}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {formulations.map((f) => (
                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gene Stack</InputLabel>
                <Select
                  value={formData.geneStackId}
                  label="Gene Stack"
                  onChange={(e) => setFormData({ ...formData, geneStackId: e.target.value })}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {geneStacks.map((g) => (
                    <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Weight/Volume"
                type="number"
                value={formData.weightVolume}
                onChange={(e) => setFormData({ ...formData, weightVolume: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Packaging Group"
                value={formData.packagingGroup}
                onChange={(e) => setFormData({ ...formData, packagingGroup: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="UN Number"
                value={formData.unNumber}
                onChange={(e) => setFormData({ ...formData, unNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Label Reference Number"
                value={formData.labelReferenceNumber}
                onChange={(e) => setFormData({ ...formData, labelReferenceNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="default-use-month-label">Default Use Month</InputLabel>
                <Select
                  labelId="default-use-month-label"
                  label="Default Use Month"
                  value={formData.defaultUseMonth}
                  onChange={(e) => setFormData({ ...formData, defaultUseMonth: e.target.value, defaultUseDay: e.target.value ? formData.defaultUseDay : '' })}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {MONTH_NAMES.map((name, i) => (
                    <MenuItem key={i + 1} value={String(i + 1)}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Default Use Day"
                value={formData.defaultUseDay}
                onChange={(e) => setFormData({ ...formData, defaultUseDay: e.target.value })}
                inputProps={{ min: 1, max: 31 }}
                disabled={!formData.defaultUseMonth}
                helperText="Used to date summary imports with no date of their own"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.dangerousGoods}
                    onChange={(e) => setFormData({ ...formData, dangerousGoods: e.target.checked })}
                  />
                }
                label="Dangerous Goods"
              />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Expected Rate (for out-of-bounds rate checking — optional)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Expected Min Rate"
                type="number"
                value={formData.expectedMinRate}
                onChange={(e) => setFormData({ ...formData, expectedMinRate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Expected Max Rate"
                type="number"
                value={formData.expectedMaxRate}
                onChange={(e) => setFormData({ ...formData, expectedMaxRate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Rate Unit</InputLabel>
                <Select
                  value={formData.expectedRateUnitId}
                  label="Rate Unit"
                  onChange={(e) => setFormData({ ...formData, expectedRateUnitId: e.target.value })}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Display Product (alias of)</InputLabel>
                <Select
                  value={formData.displayProductId}
                  label="Display Product (alias of)"
                  onChange={(e) => setFormData({ ...formData, displayProductId: e.target.value })}
                >
                  <MenuItem value=""><em>None — this is its own display product</em></MenuItem>
                  {products
                    .filter((p) => p.id !== editingProductId && !p.archivedDate)
                    .map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Only products on the current page are listed here — search for the target product first if it's not shown.
              </Typography>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Active Ingredients</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Active Ingredient</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" py={2}>
                            No active ingredients added yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      activeRows.map((pai) => (
                        <TableRow key={pai.id}>
                          <TableCell>{pai.activeIngredientName}</TableCell>
                          <TableCell>{pai.unitAmount}</TableCell>
                          <TableCell>{pai.unitName}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleRemoveActiveRow(pai)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    size="small"
                    options={activeIngredients}
                    getOptionLabel={(ai) => ai.name}
                    isOptionEqualToValue={(ai, v) => ai.id === v.id}
                    filterOptions={filterActiveIngredientOptions}
                    value={activeIngredients.find((ai) => ai.id === newAiId) ?? null}
                    onChange={(_, value) => setNewAiId(value?.id ?? '')}
                    renderInput={(params) => <TextField {...params} label="Active Ingredient" />}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Amount"
                    type="number"
                    value={newUnitAmount}
                    onChange={(e) => setNewUnitAmount(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select value={newUnitId} label="Unit" onChange={(e) => setNewUnitId(e.target.value)}>
                      {units.map((u) => (
                        <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton color="primary" onClick={handleAddActiveRow} disabled={!newAiId || !newUnitId}>
                    <AddIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Categories</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2" color="text.secondary" py={2}>
                            No categories added yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      categoryRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.productCategoryName}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleRemoveCategoryRow(row)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={10}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select value={newCategoryId} label="Category" onChange={(e) => setNewCategoryId(e.target.value)}>
                      {productCategories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton color="primary" onClick={handleAddCategoryRow} disabled={!newCategoryId}>
                    <AddIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <ProductDocumentsPanel
                productId={editingProductId}
                staged={stagedDocuments}
                onStagedChange={setStagedDocuments}
                onChanged={fetchProducts}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name}>
            {editingProductId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
